import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert, Skeleton, LinearProgress } from '@mui/material';
import { createChart, ColorType, AreaSeries } from 'lightweight-charts';
import axiosInstance from '../../../../utils/axiosInstance';
import { SignalDirectionLabels, SignalDirectionColors, SignalStrengthLabels, AlertConditionLabels, AlertStatusLabels, AlertStatusColors } from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children, syncing }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
        {syncing && <Chip label="Syncing..." size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20, color: '#f59e0b', borderColor: '#f59e0b', '& .MuiChip-label': { px: 1 } }} />}
    </Box>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SignalChip = ({ signal }) => {
    const colorMap = { BUY: 'success', SELL: 'error', NEUTRAL: 'default', LONG_VOL: 'success', SHORT_VOL: 'error' };
    return <Chip label={signal || '-'} size="small" color={colorMap[signal] || 'default'} />;
};

// Skeleton placeholder for chart area
const ChartSkeleton = () => (
    <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={60} height={20} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={260} sx={{ borderRadius: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {[...Array(7)].map((_, i) => <Skeleton key={i} variant="rounded" width="14%" height={44} />)}
        </Box>
    </Box>
);

// Skeleton for earnings grid
const EarningsSkeleton = () => (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
            {[...Array(4)].map((_, i) => (
                <Grid item xs={6} sm={3} key={i}>
                    <Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="80%" height={28} />
                </Grid>
            ))}
        </Grid>
    </Paper>
);


// Period config: label, date format for X axis
const PERIODS = [
    { key: '1D', label: '1 day', dateFmt: { hour: '2-digit', minute: '2-digit' } },
    { key: '5D', label: '5 days', dateFmt: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' } },
    { key: '1M', label: '1 month', dateFmt: { month: 'short', day: 'numeric' } },
    { key: '6M', label: '6 months', dateFmt: { month: 'short', day: 'numeric' } },
    { key: '1Y', label: '1 year', dateFmt: { month: 'short', day: 'numeric', year: '2-digit' } },
    { key: '5Y', label: '5 years', dateFmt: { month: 'short', year: '2-digit' } },
    { key: 'All', label: 'All time', dateFmt: { month: 'short', year: 'numeric' } },
];

const OverviewTab = ({ data, loading, error, symbolId, symbolData: _ignored, onRefreshOverview }) => {
    const [chartPeriod, setChartPeriod] = useState('1D');
    const [chartData, setChartData] = useState({});       // keyed by period
    const [chartLoading, setChartLoading] = useState({});
    const [liveData, setLiveData] = useState(null);       // auto-polled overview data
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const pollRef = useRef(null);

    // Use liveData (from polling) if available, otherwise use prop data
    const activeData = liveData || data;

    // Backend tells us if a backfill was triggered via `syncing` flag
    const isSyncing = activeData?.syncing === true;

    // Auto-poll overview + chart when backend says it's syncing
    useEffect(() => {
        if (!symbolId || !isSyncing) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        if (pollRef.current) return; // already polling
        pollRef.current = setInterval(async () => {
            try {
                const res = await axiosInstance.get(`/api/stock-trading/symbol-detail/${symbolId}/overview`);
                if (res.data.success) {
                    setLiveData(res.data.data);
                    if (!res.data.data.syncing) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        // Re-fetch all chart data since backfill populated it
                        setChartData({});
                    }
                }
            } catch (e) { /* ignore polling errors */ }
        }, 10000);
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [symbolId, isSyncing]);

    // Auto-poll chart when chart data is empty and backend is syncing
    const isChartEmpty = !chartData[chartPeriod] || chartData[chartPeriod]?.length === 0;

    useEffect(() => {
        if (!symbolId || !isChartEmpty) return;
        const chartPoll = setInterval(async () => {
            try {
                const res = await axiosInstance.get(`/api/stock-trading/symbol-detail/${symbolId}/chart?period=${chartPeriod}`);
                if (res.data.success && res.data.data.prices?.length > 0) {
                    setChartData(prev => ({ ...prev, [chartPeriod]: res.data.data.prices }));
                    clearInterval(chartPoll);
                }
            } catch (e) { /* ignore */ }
        }, 10000);
        return () => clearInterval(chartPoll);
    }, [symbolId, chartPeriod, isChartEmpty]);

    // Fetch chart data for a period from the dedicated endpoint
    const fetchChartData = useCallback(async (period) => {
        if (chartData[period] || chartLoading[period]) return;
        setChartLoading(prev => ({ ...prev, [period]: true }));
        try {
            const response = await axiosInstance.get(`/api/stock-trading/symbol-detail/${symbolId}/chart?period=${period}`);
            if (response.data.success) {
                setChartData(prev => ({ ...prev, [period]: response.data.data.prices || [] }));
            }
        } catch (err) {
            setChartData(prev => ({ ...prev, [period]: [] }));
        } finally {
            setChartLoading(prev => ({ ...prev, [period]: false }));
        }
    }, [symbolId, chartData, chartLoading]);

    // Fetch active period on mount and whenever period changes
    useEffect(() => {
        if (symbolId) fetchChartData(chartPeriod);
    }, [chartPeriod, symbolId, fetchChartData]);

    // Pre-fetch all periods in background so return percentages show on all tabs
    useEffect(() => {
        if (!symbolId) return;
        PERIODS.forEach(pr => fetchChartData(pr.key));
    }, [symbolId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Create or update lightweight-charts when container is ready and data changes
    useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;

        const rawPrices = chartData[chartPeriod] || [];
        if (rawPrices.length === 0) return;

        const isIntraday = ['1D', '5D'].includes(chartPeriod);
        const isUp = rawPrices.length > 1 && (rawPrices[rawPrices.length - 1].close_price >= rawPrices[0].close_price);
        const lineColor = isUp ? '#22c55e' : '#ef4444';
        const areaTopColor = isUp ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.28)';
        const areaBottomColor = isUp ? 'rgba(34,197,94,0.02)' : 'rgba(239,68,68,0.02)';

        // Create chart if it doesn't exist yet
        if (!chartRef.current) {
            const chart = createChart(container, {
                width: container.clientWidth,
                height: 300,
                layout: {
                    background: { type: ColorType.Solid, color: 'transparent' },
                    textColor: '#94a3b8',
                    fontSize: 11,
                },
                grid: {
                    vertLines: { color: '#f1f5f9' },
                    horzLines: { color: '#f1f5f9' },
                },
                rightPriceScale: { borderColor: '#e2e8f0' },
                timeScale: {
                    borderColor: '#e2e8f0',
                    timeVisible: isIntraday,
                    secondsVisible: false,
                },
                crosshair: { mode: 0 },
            });
            chartRef.current = chart;

            const handleResize = () => {
                if (container) chart.applyOptions({ width: container.clientWidth });
            };
            window.addEventListener('resize', handleResize);
            container._resizeHandler = handleResize;
        }

        // Remove old series and create new one with updated colors
        if (seriesRef.current) {
            try { chartRef.current.removeSeries(seriesRef.current); } catch (e) { /* ignore */ }
        }
        const series = chartRef.current.addSeries(AreaSeries, {
            lineColor, topColor: areaTopColor, bottomColor: areaBottomColor,
            lineWidth: 2,
            priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        });
        seriesRef.current = series;

        // Convert data for lightweight-charts (deduplicate + sort ascending by time)
        const seen = new Set();
        const chartDisplayData = rawPrices.map(p => {
            const ts = new Date(p.timestamp);
            if (isIntraday) {
                return { time: Math.floor(ts.getTime() / 1000), value: p.close_price };
            }
            return {
                time: `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}-${String(ts.getDate()).padStart(2, '0')}`,
                value: p.close_price,
            };
        }).filter(d => {
            if (d.value == null) return false;
            const key = String(d.time);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        }).sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));

        series.setData(chartDisplayData);

        chartRef.current.applyOptions({
            timeScale: { timeVisible: isIntraday, secondsVisible: false },
        });
        chartRef.current.timeScale().fitContent();
    }, [chartPeriod, chartData, data]);

    // Cleanup chart on unmount
    useEffect(() => {
        return () => {
            if (chartContainerRef.current?._resizeHandler) {
                window.removeEventListener('resize', chartContainerRef.current._resizeHandler);
            }
            if (chartRef.current) {
                chartRef.current.remove();
                chartRef.current = null;
                seriesRef.current = null;
            }
        };
    }, []);


    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!activeData) return <TabLoading />;

    const sym = activeData.symbol || {};
    const signals = activeData.signals || [];
    const alerts = activeData.alerts || [];
    const nextEarnings = activeData.next_earnings;

    const rawPrices = chartData[chartPeriod] || [];
    const syncing = isSyncing || isChartEmpty;

    // Calculate return for each period (from cached data)
    const getPeriodReturn = (prices) => {
        if (!prices || prices.length < 2) return null;
        const first = prices[0].close_price;
        const last = prices[prices.length - 1].close_price;
        if (!first) return null;
        return { pct: (last - first) / first * 100 };
    };

    return (
        <Box>
            {/* Sync progress bar */}
            {syncing && <LinearProgress sx={{ mb: 1, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' } }} />}

            {/* ===== Charts Section ===== */}
            <SectionTitle syncing={syncing && rawPrices.length === 0}>Charts</SectionTitle>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                {chartLoading[chartPeriod] ? (
                    <ChartSkeleton />
                ) : rawPrices.length === 0 ? (
                    <ChartSkeleton />
                ) : null}
                <div ref={chartContainerRef} style={{ width: '100%', minHeight: rawPrices.length > 0 ? 300 : 0 }} />

                {/* Period Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {PERIODS.map((pr) => {
                        const ret = getPeriodReturn(chartData[pr.key]);
                        return (
                            <Box
                                key={pr.key}
                                onClick={() => setChartPeriod(pr.key)}
                                sx={{
                                    flex: 1, minWidth: 80, textAlign: 'center', py: 1, px: 1,
                                    borderRadius: 1, cursor: 'pointer',
                                    bgcolor: chartPeriod === pr.key ? '#fff7ed' : 'transparent',
                                    border: chartPeriod === pr.key ? '1px solid #f59e0b' : '1px solid transparent',
                                    '&:hover': { bgcolor: '#fff7ed' },
                                }}
                            >
                                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>
                                    {pr.label}
                                </Typography>
                                {ret?.pct != null ? (
                                    <Typography variant="caption" sx={{ color: ret.pct >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                                        {ret.pct >= 0 ? '+' : ''}{ret.pct.toFixed(2)}%
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        {chartLoading[pr.key] ? '...' : '-'}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            {/* ===== Upcoming Earnings ===== */}
            <SectionTitle syncing={syncing && (!nextEarnings || nextEarnings.estimated_eps == null)}>Upcoming earnings</SectionTitle>
            {!nextEarnings && syncing ? (
                <EarningsSkeleton />
            ) : nextEarnings ? (
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Next report date</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.earnings_date ? `~ ${new Date(nextEarnings.earnings_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Report period</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.fiscal_quarter && nextEarnings.fiscal_year ? `${nextEarnings.fiscal_quarter} ${nextEarnings.fiscal_year}` : syncing ? <Skeleton variant="text" width={80} /> : '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>EPS estimate</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.estimated_eps != null ? <>{nextEarnings.estimated_eps} <Typography component="span" variant="caption" color="text.secondary">USD</Typography></> : syncing ? <Skeleton variant="text" width={60} /> : '\u2014'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Revenue estimate</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {nextEarnings.estimated_revenue != null ? <>{(nextEarnings.estimated_revenue / 1e6).toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">M USD</Typography></> : syncing ? <Skeleton variant="text" width={80} /> : '\u2014'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            ) : (
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <EmptyState message="No upcoming earnings scheduled" />
                </Paper>
            )}

            {/* ===== Key Stats ===== */}
            <SectionTitle>Key stats</SectionTitle>
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    {[
                        { label: 'Market capitalization', value: sym.market_cap ? `${(sym.market_cap / 1e9).toFixed(2)} B` : '\u2014', unit: 'USD' },
                        { label: 'Previous Close', value: sym.previous_close ? sym.previous_close.toFixed(2) : '\u2014', unit: 'USD' },
                        { label: 'Day High', value: sym.day_high ? sym.day_high.toFixed(2) : '\u2014', unit: 'USD' },
                        { label: 'Day Low', value: sym.day_low ? sym.day_low.toFixed(2) : '\u2014', unit: 'USD' },
                        { label: 'Volume', value: sym.volume ? Number(sym.volume).toLocaleString() : '\u2014', unit: '' },
                        { label: 'Sector', value: sym.sector || '\u2014', unit: '' },
                        { label: 'Industry', value: sym.industry || '\u2014', unit: '' },
                        { label: 'Open', value: sym.day_open ? sym.day_open.toFixed(2) : '\u2014', unit: 'USD' },
                    ].map((item, i) => (
                        <Grid item xs={6} sm={3} key={i}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary">{item.label}</Typography>
                            <Typography variant="h6" fontWeight={500}>
                                {item.value} {item.unit && <Typography component="span" variant="caption" color="text.secondary">{item.unit}</Typography>}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Signals */}
            <SectionTitle>Signals</SectionTitle>
            {signals.length === 0 ? <EmptyState message="No signals available" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Direction</TableCell>
                                <TableCell>Strength</TableCell>
                                <TableCell align="right">Confidence</TableCell>
                                <TableCell>Generated At</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {signals.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={SignalDirectionLabels[row.direction] || row.direction} size="small" color={SignalDirectionColors[row.direction] || 'default'} />
                                    </TableCell>
                                    <TableCell>{SignalStrengthLabels[row.strength] || row.strength || '-'}</TableCell>
                                    <TableCell align="right">{formatPercent(row.confidence_score)}</TableCell>
                                    <TableCell>{formatDateTime(row.generated_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Alerts */}
            <SectionTitle>Price Alerts</SectionTitle>
            {alerts.length === 0 ? <EmptyState message="No alerts configured" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Condition</TableCell>
                                <TableCell align="right">Target Price</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {alerts.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{AlertConditionLabels[row.condition] || row.condition}</TableCell>
                                    <TableCell align="right">{formatPrice(row.target_price)}</TableCell>
                                    <TableCell>
                                        <Chip label={AlertStatusLabels[row.status] || row.status} size="small" color={AlertStatusColors[row.status] || 'default'} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default OverviewTab;
